import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Save, Trash2, Plus, ExternalLink, Bot, ShieldCheck, ShieldOff, MessageSquare } from "lucide-react";

/** GEO tab — Generative Engine Optimization.
 *  Manages /llms.txt content (brand story + FAQs) and AI crawler allow/deny policy. */
export default function AdminGEO() {
    const [g, setG] = useState({ brand_story: "", brand_pitch: "", contact_email: "", crawler_policies: [], known_bots: [] });
    const [faqs, setFaqs] = useState([]);
    const [saving, setSaving] = useState(false);
    const [storySeed, setStorySeed] = useState("");
    const [faqSeed, setFaqSeed] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const [newFaq, setNewFaq] = useState({ category: "General", question: "", answer: "", order: 0 });

    const load = async () => {
        const gd = await api.get("/admin/geo");
        setG((prev) => ({ ...prev, ...gd.data }));
        const fd = await api.get("/admin/geo/faqs");
        setFaqs(fd.data.rows || []);
    };
    useEffect(() => { load(); }, []);

    const saveSettings = async () => {
        setSaving(true);
        try {
            await api.put("/admin/geo", {
                brand_story: g.brand_story,
                brand_pitch: g.brand_pitch,
                contact_email: g.contact_email,
            });
            toast.success("Brand story saved. llms.txt will update immediately.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const generateStory = async () => {
        setAiBusy(true);
        try {
            const { data } = await api.post("/admin/geo/write-story", { seed: storySeed });
            setG((prev) => ({
                ...prev,
                brand_story: data.brand_story || prev.brand_story,
                brand_pitch: data.brand_pitch || prev.brand_pitch,
            }));
            toast.success("Story generated. Save to publish.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI request failed");
        } finally { setAiBusy(false); }
    };

    const generateFaqs = async () => {
        if (!faqSeed.trim()) { toast.error("Enter a topic first"); return; }
        setAiBusy(true);
        try {
            const { data } = await api.post("/admin/geo/write-faqs", { seed: faqSeed, count: 8 });
            const generated = data.faqs || [];
            // Immediately save each one
            for (const f of generated) {
                await api.post("/admin/geo/faqs", { category: f.category, question: f.question, answer: f.answer, order: faqs.length });
            }
            toast.success(`${generated.length} FAQ pairs added`);
            setFaqSeed("");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI request failed");
        } finally { setAiBusy(false); }
    };

    const saveFaq = async (faq) => {
        if (!faq.question.trim() || !faq.answer.trim()) { toast.error("Question and answer required"); return; }
        await api.post("/admin/geo/faqs", faq);
        toast.success("FAQ saved");
        if (!faq.id) setNewFaq({ category: "General", question: "", answer: "", order: faqs.length });
        load();
    };
    const deleteFaq = async (id) => {
        if (!window.confirm("Delete this FAQ?")) return;
        await api.delete(`/admin/geo/faqs/${id}`);
        load();
    };

    const togglePolicy = async (bot, currentAllow) => {
        await api.put("/admin/geo/crawler-policy", { bot, allow: !currentAllow });
        load();
    };
    const deletePolicy = async (bot) => {
        await api.delete("/admin/geo/crawler-policy", { params: { bot } });
        load();
    };

    const policyFor = (bot) => (g.crawler_policies || []).find((p) => (p.bot || "").toLowerCase() === bot.toLowerCase());

    return (
        <div className="space-y-6" data-testid="admin-geo-page">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-purple-700">Generative Engine Optimization</div>
                <h1 className="font-serif text-4xl text-stone-900">GEO</h1>
                <p className="text-sm text-stone-500 mt-1">
                    Curate the brand story, FAQs, and AI-crawler policies that shape how ChatGPT, Claude, Perplexity & Google's AI describe your brand.
                    &nbsp;<a href="/api/llms.txt" target="_blank" rel="noreferrer" className="text-sky-700 underline">View live /llms.txt <ExternalLink className="w-3 h-3 inline" /></a>
                </p>
            </div>

            <Tabs defaultValue="story">
                <TabsList className="rounded-none">
                    <TabsTrigger value="story" className="rounded-none" data-testid="geo-tab-story">Brand story</TabsTrigger>
                    <TabsTrigger value="faqs" className="rounded-none" data-testid="geo-tab-faqs">FAQ packs</TabsTrigger>
                    <TabsTrigger value="policy" className="rounded-none" data-testid="geo-tab-policy">AI crawler policy</TabsTrigger>
                </TabsList>

                {/* Brand story */}
                <TabsContent value="story" className="mt-4 space-y-4">
                    <div className="bg-white border border-stone-200 p-5 space-y-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-stone-500">Brand pitch (1 line, ~180 chars)</label>
                            <Textarea value={g.brand_pitch || ""} onChange={(e) => setG((prev) => ({ ...prev, brand_pitch: e.target.value }))} rows={2} className="rounded-none border-stone-300 mt-1" data-testid="geo-brand-pitch" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-stone-500">Brand story (3-5 paragraphs)</label>
                            <Textarea value={g.brand_story || ""} onChange={(e) => setG((prev) => ({ ...prev, brand_story: e.target.value }))} rows={12} className="rounded-none border-stone-300 mt-1" data-testid="geo-brand-story" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-stone-500">Contact email for AI-referred visitors</label>
                            <Input value={g.contact_email || ""} onChange={(e) => setG((prev) => ({ ...prev, contact_email: e.target.value }))} placeholder="help@hplmedicines.com" className="rounded-none border-stone-300 mt-1" data-testid="geo-contact-email" />
                        </div>

                        <div className="border-t border-stone-200 pt-4 space-y-2">
                            <div className="text-xs uppercase tracking-widest text-stone-500 flex items-center gap-2"><Sparkles className="w-3 h-3 text-purple-600" />Regenerate with AI</div>
                            <Textarea rows={2} value={storySeed} onChange={(e) => setStorySeed(e.target.value)} placeholder="Optional seed context — e.g. 'Family-run since 1976, exports to Nepal & Bhutan, 500+ SKUs'" className="rounded-none border-stone-300" data-testid="geo-story-seed" />
                            <Button onClick={generateStory} disabled={aiBusy} className="rounded-none bg-purple-700 hover:bg-purple-800 text-stone-50" data-testid="geo-story-ai">
                                <Sparkles className="w-4 h-4 mr-2" />{aiBusy ? "Thinking…" : "Rewrite story with AI"}
                            </Button>
                        </div>
                    </div>
                    <Button onClick={saveSettings} disabled={saving} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="geo-save">
                        <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save & publish"}
                    </Button>
                </TabsContent>

                {/* FAQ packs */}
                <TabsContent value="faqs" className="mt-4 space-y-4">
                    <div className="bg-white border border-stone-200 p-5 space-y-3">
                        <div className="text-xs uppercase tracking-widest text-stone-500 flex items-center gap-2"><Sparkles className="w-3 h-3 text-purple-600" />Generate an FAQ pack</div>
                        <div className="flex gap-2">
                            <Input value={faqSeed} onChange={(e) => setFaqSeed(e.target.value)} placeholder="e.g. 'shipping and returns', 'how homeopathy consults work', 'distributor onboarding'" className="rounded-none border-stone-300 flex-1" data-testid="geo-faq-seed" />
                            <Button onClick={generateFaqs} disabled={aiBusy} className="rounded-none bg-purple-700 hover:bg-purple-800 text-stone-50" data-testid="geo-faq-ai">
                                <Sparkles className="w-4 h-4 mr-2" />{aiBusy ? "Generating…" : "Generate pack"}
                            </Button>
                        </div>
                    </div>

                    <div className="bg-white border border-stone-200 p-4 space-y-3">
                        <div className="text-xs uppercase tracking-widest text-stone-500">Add a single FAQ</div>
                        <div className="grid md:grid-cols-3 gap-2">
                            <Input value={newFaq.category} onChange={(e) => setNewFaq((p) => ({ ...p, category: e.target.value }))} placeholder="Category" className="rounded-none border-stone-300" data-testid="geo-newfaq-cat" />
                            <Input value={newFaq.question} onChange={(e) => setNewFaq((p) => ({ ...p, question: e.target.value }))} placeholder="Question" className="rounded-none border-stone-300 md:col-span-2" data-testid="geo-newfaq-q" />
                        </div>
                        <Textarea rows={3} value={newFaq.answer} onChange={(e) => setNewFaq((p) => ({ ...p, answer: e.target.value }))} placeholder="Answer" className="rounded-none border-stone-300" data-testid="geo-newfaq-a" />
                        <Button onClick={() => saveFaq(newFaq)} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="geo-newfaq-save">
                            <Plus className="w-4 h-4 mr-2" />Add FAQ
                        </Button>
                    </div>

                    <div className="bg-white border border-stone-200">
                        <div className="p-4 text-xs uppercase tracking-widest text-stone-500 border-b border-stone-200 flex items-center gap-2"><MessageSquare className="w-4 h-4" />Existing FAQs ({faqs.length})</div>
                        {faqs.length === 0 ? (
                            <div className="p-8 text-center text-stone-400 text-sm">No FAQs yet. Generate a pack above or add one manually.</div>
                        ) : (
                            <ul className="divide-y divide-stone-100">
                                {faqs.map((f) => (
                                    <li key={f.id} className="p-4 hover:bg-sky-50/40" data-testid={`geo-faq-row-${f.id}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1">
                                                <div className="text-[10px] uppercase tracking-widest text-purple-700 mb-1">{f.category}</div>
                                                <div className="font-medium text-stone-900">{f.question}</div>
                                                <div className="text-sm text-stone-600 mt-1 whitespace-pre-line">{f.answer}</div>
                                            </div>
                                            <button onClick={() => deleteFaq(f.id)} className="text-red-600 hover:bg-red-50 p-1.5 border border-red-200 shrink-0" data-testid={`geo-faq-del-${f.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </TabsContent>

                {/* Crawler policy */}
                <TabsContent value="policy" className="mt-4 space-y-4">
                    <div className="bg-white border border-stone-200 p-5">
                        <p className="text-sm text-stone-600 mb-4">Toggle each AI crawler's access. Deny (red) adds <code>Disallow: /</code> to robots.txt for that bot; Allow (green) explicitly permits crawling. Unset = default site policy applies.</p>
                        <div className="grid md:grid-cols-2 gap-2">
                            {(g.known_bots || []).map((bot) => {
                                const p = policyFor(bot);
                                const state = p ? (p.allow ? "allow" : "deny") : "unset";
                                return (
                                    <div key={bot} className="border border-stone-200 p-3 flex items-center justify-between" data-testid={`geo-bot-row-${bot}`}>
                                        <div className="flex items-center gap-2">
                                            <Bot className="w-4 h-4 text-purple-700" />
                                            <div>
                                                <div className="font-mono text-sm text-stone-900">{bot}</div>
                                                {p?.updated_at && <div className="text-[10px] text-stone-400">Last set {(p.updated_at || "").slice(0, 10)}</div>}
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => togglePolicy(bot, false)}
                                                className={`text-xs px-2 py-1 border ${state === "allow" ? "bg-emerald-600 text-white border-emerald-600" : "border-stone-300 hover:bg-emerald-50"}`}
                                                data-testid={`geo-bot-allow-${bot}`}
                                            >
                                                <ShieldCheck className="w-3 h-3 inline mr-1" />Allow
                                            </button>
                                            <button
                                                onClick={() => togglePolicy(bot, true)}
                                                className={`text-xs px-2 py-1 border ${state === "deny" ? "bg-red-600 text-white border-red-600" : "border-stone-300 hover:bg-red-50"}`}
                                                data-testid={`geo-bot-deny-${bot}`}
                                            >
                                                <ShieldOff className="w-3 h-3 inline mr-1" />Deny
                                            </button>
                                            {p && (
                                                <button onClick={() => deletePolicy(bot)} className="text-xs px-2 py-1 border border-stone-300 hover:bg-stone-50" data-testid={`geo-bot-clear-${bot}`}>
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
